import React, { useState } from "react";
import { message, Row, Col } from "antd";
import Dropzone from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import { useMutation } from "@apollo/client";

// utilities
import { FileChecksum } from "@rails/activestorage/src/file_checksum";
import { BlobUpload } from "@rails/activestorage/src/blob_upload";

// graphql
import CREATE_DIRECT_UPLOAD from "graphql/mutations/CreateDirectUpload";
import UPLOAD_STUDENT_AVATAR from "graphql/mutations/UploadStudentAvatar";
import { GET_PROFILE_AVATAR } from "graphql/queries/GetProfile";
import * as Styled from "./UploadAvatarStyles";

// Flow Direct Uploading:
// 1. Use `createDirectUpload` mutation to get:
//      - signedBlobId of the file
//      - an URL for direct uploading.
//          If it's dev env, URL will start with localhost:3000/....
//          If it's prod env, URL will be AWS S3
// 2. PUT the file to the URL returned from step 1 to upload it to the server
// 3. Use `uploadStudentAvatar` mutation with signedBlobId from step 1 as input
//    to set the uploaded file as student avatar image field.

// Read more about Direct uploading:
// https://evilmartians.com/chronicles/active-storage-meets-graphql-direct-uploads

interface UploadAvatarProps {
  originalSrc: string;
}

const UploadAvatar = (props: UploadAvatarProps) => {
  const { t } = useTranslation("my_profile");

  const [selectedFile, setSelectedFile] = useState<File | undefined>();

  /**
   * directUpload
   * PUT a @file to an @url, using @headers
   * @param {string} url
   * @param {*} headers
   * @param {*} file
   */
  const directUpload = (url: string, headers: any, file: any) => {
    const upload = new BlobUpload({ file, directUploadData: { url, headers } });
    return new Promise((resolve, reject) => {
      upload.create((error: any) => {
        if (error) {
          reject(error);
        } else {
          resolve(true);
        }
      });
    });
  };

  const [uploadStudentAvatar, { loading: uploadLoading }] = useMutation(
    UPLOAD_STUDENT_AVATAR,
    {
      onCompleted: () => {
        message.success(t("Image uploaded successfully!"));
      },
      onError: () => {
        message.error(t("Failed uploading image!"));
      },
      refetchQueries: [{ query: GET_PROFILE_AVATAR }],
    }
  );

  const [createDirectUpload, { loading: getBlobIdLoading }] = useMutation(
    CREATE_DIRECT_UPLOAD,
    {
      onCompleted: (data) => {
        const {
          createDirectUpload: {
            directUpload: { url, headers, signedBlobId },
          },
        } = data;
        directUpload(url, JSON.parse(headers), selectedFile).then(() => {
          uploadStudentAvatar({
            variables: {
              input: {
                blobId: signedBlobId,
              },
            },
          });
        });
      },
      onError: () => {
        message.error("Uploaded Image failed!");
        console.error("Direct upload failed");
      },
    }
  );

  // ----------------
  // HANDLERS
  // ----------------
  /**
   * handleSelectFile
   * Called after a file is selected.
   * @param {*} [file]
   */
  const handleSelectFile = ([file]: any) => {
    setSelectedFile(file);
    FileChecksum.create(file, (_: undefined, checksum: string) => {
      // Get the MD5 checksum of the file
      // Then use it as input for createDirectUpload mutation
      createDirectUpload({
        variables: {
          input: {
            attributes: {
              filename: file.name,
              byteSize: file.size,
              checksum,
              contentType: file.type,
            },
          },
        },
      });
    });
  };

  const { originalSrc } = props;

  // RENDERERS
  const renderUploadButton = () => {
    return originalSrc ? (
      <Styled.ReplacePhotoButton loading={getBlobIdLoading || uploadLoading}>
        {t("REPLACE PHOTO")}
      </Styled.ReplacePhotoButton>
    ) : (
      <Styled.UploadPhotoButton loading={getBlobIdLoading || uploadLoading}>
        {t("UPLOAD PHOTO")}
      </Styled.UploadPhotoButton>
    );
  };

  return (
    <Row>
      <Col sm={24} md={6}>
        <Styled.Avatar
          size={100}
          icon={<FontAwesomeIcon icon={faUser as IconProp} />}
          src={originalSrc}
        />
      </Col>
      <Col sm={24} md={{ span: 6, offset: 7 }}>
        <Styled.VerticalCenterContainer>
          <Dropzone noDrag multiple={false} onDrop={handleSelectFile}>
            {({ getRootProps, getInputProps }) => (
              <section>
                <div // eslint-disable-next-line react/jsx-props-no-spreading
                  {...getRootProps()}
                >
                  <input // eslint-disable-next-line react/jsx-props-no-spreading
                    {...getInputProps()}
                  />
                  {renderUploadButton()}
                </div>
              </section>
            )}
          </Dropzone>
        </Styled.VerticalCenterContainer>
      </Col>
    </Row>
  );
};

export default UploadAvatar;
